import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx-js-style';
import {
  ICliente,
  IDetalleClienteFacturacion,
  IFacturacionEnCurso,
  IQueryParam,
  IResumenDatosBase,
} from 'modelos/src';
import { ResumenesDatosService } from '../../../auxiliares/servicios/http/resumenes-datos.service';
import { GastosService } from '../../../auxiliares/servicios/http/gastos.service';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { LoginService } from '../../login/login.service';
import { ChartCardConfig } from '../../../componentes/chart-card/chart-card.component';

type IResumenGasto = IResumenDatosBase<'Gastos del Cliente'>;

@Component({
  selector: 'app-gastos',
  templateUrl: './gastos.component.html',
  styleUrl: './gastos.component.scss',
  standalone: false,
})
export class GastosComponent implements OnInit, OnDestroy {
  private helper = inject(HelperService);
  private resumenesService = inject(ResumenesDatosService);
  private gastosService = inject(GastosService);

  // Estado de carga (loading=true al iniciar, false al terminar el fetch)
  public loadingFacturacion = true;
  public loadingHistorial = true;

  // Categorías de dispositivos facturables que detalla el historial
  public categorias: {
    key: 'trackers' | 'alarmas' | 'camaras';
    label: string;
  }[] = [
    { key: 'trackers', label: 'Trackers' },
    { key: 'alarmas', label: 'Alarmas' },
    { key: 'camaras', label: 'Cámaras' },
  ];

  // Datos
  public cliente?: ICliente;
  /** Facturación en curso (estimado vs real del mes). */
  public facturacion?: IFacturacionEnCurso;
  /** Gráfico de facturación en curso. */
  public chartFacturacion?: ChartCardConfig;
  /** Historial de resúmenes de gastos ya facturados, más reciente primero. */
  public historial: IResumenGasto[] = [];
  /** Ventana fija de 6 meses, terminando en el mes elegido. */
  public readonly tamanoVentana = 6;
  /** Mes (0-based) y año finales de la ventana de 6 meses. */
  public mesSel = 0;
  public anioSel = new Date().getFullYear();
  public aniosOpciones: number[] = [];
  /** Gráfico apilado del histórico (por mes, segmentado por categoría). */
  public chartHistorico?: ChartCardConfig;
  private mesesCortos = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ];
  private mesesLargos = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  /** Nombre del mes en curso. */
  public get mesActualNombre(): string {
    return this.mesesLargos[new Date().getMonth()];
  }

  /** Nombre del mes anterior al actual. */
  public get mesAnteriorNombre(): string {
    return this.mesesLargos[(new Date().getMonth() + 11) % 12];
  }

  async ngOnInit(): Promise<void> {
    this.cliente = LoginService.getCliente();
    // Default: ventana terminando en el mes anterior.
    const ahora = new Date();
    const prev = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
    this.mesSel = prev.getMonth();
    this.anioSel = prev.getFullYear();
    const anio = ahora.getFullYear();
    this.aniosOpciones = [anio, anio - 1, anio - 2, anio - 3];
    await Promise.all([
      this.cargarFacturacionEnCurso(),
      this.cargarHistorial(),
    ]);
  }

  /** Opciones de mes para el selector (0-based). */
  public get mesesOpciones(): { valor: number; nombre: string }[] {
    return this.mesesCortos.map((nombre, valor) => ({ valor, nombre }));
  }

  /** Hijos mayoristas, cada uno como fila individual. */
  public get hijosMayoristas(): IDetalleClienteFacturacion[] {
    return (this.facturacion?.hijos ?? []).filter(
      (h) => h.tipoCliente === 'Mayorista',
    );
  }

  // Orden de la tabla de hijos.
  public ordenHijosCampo: 'nombre' | 'costoPasado' | 'costoReal' = 'costoReal';
  public ordenHijosDir: 'asc' | 'desc' = 'desc';

  /** Hijos mayoristas ordenados según la columna elegida. */
  public get hijosMayoristasOrdenados(): IDetalleClienteFacturacion[] {
    const campo = this.ordenHijosCampo;
    const factor = this.ordenHijosDir === 'asc' ? 1 : -1;
    return [...this.hijosMayoristas].sort((a, b) => {
      if (campo === 'nombre') {
        return (a.nombre ?? '').localeCompare(b.nombre ?? '') * factor;
      }
      const va = campo === 'costoPasado' ? (a.costoPasado ?? 0) : a.costoReal;
      const vb = campo === 'costoPasado' ? (b.costoPasado ?? 0) : b.costoReal;
      return (va - vb) * factor;
    });
  }

  /** Hijos con su detalle de cantidades desplegado. */
  public hijosExpandidos = new Set<string>();

  public toggleHijo(id?: string): void {
    if (!id) {
      return;
    }
    if (this.hijosExpandidos.has(id)) {
      this.hijosExpandidos.delete(id);
    } else {
      this.hijosExpandidos.add(id);
    }
  }

  public ordenarHijos(campo: 'nombre' | 'costoPasado' | 'costoReal'): void {
    if (this.ordenHijosCampo === campo) {
      this.ordenHijosDir = this.ordenHijosDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.ordenHijosCampo = campo;
      this.ordenHijosDir = 'asc';
    }
  }

  /** Resto de hijos (finales/otros) agrupados en una sola fila. */
  public get clientesFinales(): {
    cantidad: number;
    costoPasado: number;
    costoReal: number;
    cantidades: IDetalleClienteFacturacion['cantidades'];
    pasado: IDetalleClienteFacturacion['pasado'];
  } | null {
    const otros = (this.facturacion?.hijos ?? []).filter(
      (h) => h.tipoCliente !== 'Mayorista',
    );
    if (!otros.length) {
      return null;
    }
    return {
      cantidad: otros.length,
      costoPasado: otros.reduce((s, h) => s + (h.costoPasado ?? 0), 0),
      costoReal: otros.reduce((s, h) => s + h.costoReal, 0),
      // Cantidades agregadas de todos los clientes finales.
      cantidades: {
        trackers: {
          total: otros.reduce(
            (s, h) => s + (h.cantidades?.trackers?.total ?? 0),
            0,
          ),
          activos: otros.reduce(
            (s, h) => s + (h.cantidades?.trackers?.activos ?? 0),
            0,
          ),
        },
        alarmas: {
          total: otros.reduce(
            (s, h) => s + (h.cantidades?.alarmas?.total ?? 0),
            0,
          ),
          activos: otros.reduce(
            (s, h) => s + (h.cantidades?.alarmas?.activos ?? 0),
            0,
          ),
        },
        camaras: {
          total: otros.reduce(
            (s, h) => s + (h.cantidades?.camaras?.total ?? 0),
            0,
          ),
        },
      },
      // Desglose del mes pasado agregado (cantidades de resumen.hijos).
      pasado: {
        trackers: {
          cantidad: otros.reduce(
            (s, h) => s + (h.pasado?.trackers?.cantidad ?? 0),
            0,
          ),
        },
        alarmas: {
          cantidad: otros.reduce(
            (s, h) => s + (h.pasado?.alarmas?.cantidad ?? 0),
            0,
          ),
        },
        camaras: {
          cantidad: otros.reduce(
            (s, h) => s + (h.pasado?.camaras?.cantidad ?? 0),
            0,
          ),
        },
      },
    };
  }

  /**
   * Trae la facturación en curso y arma el gráfico de 2 series:
   * estimado (fantasma) vs real (lo que seguro se factura).
   */
  private async cargarFacturacionEnCurso(): Promise<void> {
    this.loadingFacturacion = true;
    try {
      this.facturacion = await this.gastosService.getFacturacionEnCurso();
      this.chartFacturacion = this.construirChart(this.facturacion);
    } catch (error) {
      console.error('Error cargando facturación en curso', error);
      this.helper.notifError('No se pudo cargar la facturación en curso');
    } finally {
      this.loadingFacturacion = false;
    }
  }

  private construirChart(f: IFacturacionEnCurso): ChartCardConfig {
    return {
      title: 'Dispositivos en curso',
      icon: 'trending_up',
      color: 'blue',
      chartHeight: '360px',
      chartOptions: {
        chart: { type: 'column' },
        xAxis: { categories: ['Trackers', 'Alarmas', 'Cámaras'] },
        yAxis: {
          min: 0,
          title: { text: 'Dispositivos' },
        },
        tooltip: {
          shared: true,
          // Muestra la cantidad de dispositivos por serie.
          formatter: function (this: any): string {
            let s = `<b>${this.x}</b>`;
            for (const p of this.points ?? []) {
              const cant = (p.y ?? 0).toLocaleString('es-AR');
              s +=
                `<br/><span style="color:${p.color}">●</span> ` +
                `${p.series.name}: <b>${cant} disp.</b>`;
            }
            return s;
          },
        },
        plotOptions: {
          // Columnas anchas (mismo tamaño) solapadas un poco: grouping:false
          // + mismo pointPadding (mismo ancho) + pointPlacement con offset.
          column: {
            grouping: false,
            pointPadding: 0.28,
            groupPadding: 0.1,
            borderRadius: 4,
            borderWidth: 1,
            borderColor: '#fff',
            dataLabels: { enabled: false },
          },
        },
        // Orden ascendente: Total → Mes anterior → Consolidado (al frente).
        series: [
          {
            name: 'Total',
            type: 'column',
            color: '#90a4ae',
            zIndex: 0,
            pointPlacement: -0.18,
            data: [
              { y: f.trackers.total },
              { y: f.alarmas.total },
              // Cámaras cuentan completas (no por reportadas) → sin "total" aparte.
              { y: null },
            ],
          },
          {
            name: 'Mes anterior',
            type: 'column',
            color: '#1565c0',
            zIndex: 1,
            pointPlacement: 0,
            data: [
              { y: f.mesPasado.trackers },
              { y: f.mesPasado.alarmas },
              { y: f.mesPasado.camaras },
            ],
          },
          {
            name: 'Consolidado',
            type: 'column',
            color: '#2e7d32',
            zIndex: 2,
            pointPlacement: 0.18,
            data: [
              { y: f.trackers.activos },
              { y: f.alarmas.activos },
              { y: f.camaras.activos },
            ],
          },
        ],
        legend: { enabled: true },
        credits: { enabled: false },
        exporting: { enabled: false },
      },
    };
  }

  /** Carga el historial de resúmenes de gastos del cliente. */
  private async cargarHistorial(): Promise<void> {
    this.loadingHistorial = true;
    try {
      const query: IQueryParam = {
        filter: JSON.stringify({ tipo: 'Gastos del Cliente' }),
        select: 'resumen periodoInicio periodoFin idCliente',
        sort: '-periodoInicio',
        limit: 50,
      };
      const data =
        await this.resumenesService.getFiltered<'Gastos del Cliente'>(query);
      this.historial = data.datos ?? [];
      this.construirChartHistorico();
    } catch (error) {
      console.error('Error cargando historial de gastos', error);
      this.helper.notifError('No se pudo cargar el historial de gastos');
    } finally {
      this.loadingHistorial = false;
    }
  }

  /**
   * Slots de los últimos N meses (calendario), terminando en el mes anterior.
   * Cada slot trae su resumen si existe (si no, queda undefined = "sin resumen").
   */
  public get mesesSlots(): {
    anio: number;
    mes: number;
    label: string;
    resumen?: IResumenGasto;
  }[] {
    const slots = [];
    // Ventana de 6 meses terminando en (anioSel, mesSel).
    for (let k = this.tamanoVentana - 1; k >= 0; k--) {
      const d = new Date(this.anioSel, this.mesSel - k, 1);
      const anio = d.getFullYear();
      const mes = d.getMonth(); // 0-based
      const resumen = this.historial.find((r) => {
        if (!r.periodoInicio) {
          return false;
        }
        const rd = new Date(r.periodoInicio);
        return rd.getFullYear() === anio && rd.getMonth() === mes;
      });
      slots.push({
        anio,
        mes,
        label: `${this.mesesCortos[mes]} ${anio}`,
        resumen,
      });
    }
    return slots;
  }

  public onVentanaChange(): void {
    this.construirChartHistorico();
  }

  /** Slots del detalle en orden descendente (más nuevo primero). */
  public get mesesSlotsDescendente(): {
    anio: number;
    mes: number;
    label: string;
    resumen?: IResumenGasto;
  }[] {
    return [...this.mesesSlots].reverse();
  }

  /**
   * Exporta el resumen de un mes a PDF: página 1 con el resumen completo
   * (categorías + total) y página 2 con el detalle por cliente hijo.
   */
  public exportarMes(resumen: IResumenGasto, label: string): void {
    const r = resumen.resumen;
    const fecha = (iso?: string) =>
      iso
        ? new Date(iso).toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
        : '';

    const doc = new jsPDF();

    // ── Página 1: resumen del mes ──
    doc.setFontSize(16);
    doc.text(`Resumen de gastos — ${label}`, 14, 18);
    doc.setFontSize(10);
    doc.text(`Cliente: ${this.cliente?.nombre ?? ''}`, 14, 26);
    doc.text(
      `Período: ${fecha(resumen.periodoInicio)} — ${fecha(resumen.periodoFin)}`,
      14,
      32,
    );
    autoTable(doc, {
      startY: 40,
      head: [['Categoría', 'Facturados', 'Total']],
      body: this.categorias.map((cat) => [
        cat.label,
        `${r?.[cat.key]?.cantidadFacturada ?? 0}`,
        `${r?.[cat.key]?.cantidadTotal ?? 0}`,
      ]),
      foot: [['Total dispositivos', `${r?.total ?? 0}`, '']],
    });

    // ── Página 2: detalle por cliente hijo ──
    doc.addPage();
    doc.setFontSize(14);
    doc.text(`Detalle por cliente — ${label}`, 14, 18);
    const hijos = r?.hijos ?? [];
    if (hijos.length) {
      autoTable(doc, {
        startY: 26,
        head: [['Cliente', 'Trackers', 'Alarmas', 'Cámaras', 'Total']],
        body: hijos.map((h) => [
          h.nombre || h.idCliente || '',
          `${h.trackers?.cantidad ?? 0}`,
          `${h.alarmas?.cantidad ?? 0}`,
          `${h.camaras?.cantidad ?? 0}`,
          `${h.total ?? 0}`,
        ]),
      });
    } else {
      doc.setFontSize(10);
      doc.text('Sin detalle por cliente en este resumen.', 14, 28);
    }

    doc.save(`gastos-${label.replace(/\s+/g, '-').toLowerCase()}.pdf`);
  }

  /**
   * Exporta el resumen de un mes a Excel: hoja "Resumen" (categorías + total)
   * y hoja "Detalle por cliente" (gasto por hijo).
   */
  public exportarMesExcel(resumen: IResumenGasto, label: string): void {
    const r = resumen.resumen;
    const fecha = (iso?: string) =>
      iso
        ? new Date(iso).toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
        : '';

    // ── Hoja 1: resumen del mes ──
    const filasResumen: (string | number)[][] = [
      ['Resumen de dispositivos', label],
      ['Cliente', this.cliente?.nombre ?? ''],
      [
        'Período',
        `${fecha(resumen.periodoInicio)} — ${fecha(resumen.periodoFin)}`,
      ],
      [],
      ['Categoría', 'Facturados', 'Total dispositivos'],
      ...this.categorias.map((cat) => [
        cat.label,
        r?.[cat.key]?.cantidadFacturada ?? 0,
        r?.[cat.key]?.cantidadTotal ?? 0,
      ]),
      [],
      ['Total dispositivos', r?.total ?? 0, ''],
    ];
    const hojaResumen = XLSX.utils.aoa_to_sheet(filasResumen);
    hojaResumen['!cols'] = [{ wch: 20 }, { wch: 14 }, { wch: 18 }];

    // ── Hoja 2: detalle por cliente hijo (dispositivos por categoría) ──
    const hijos = r?.hijos ?? [];
    const filasHijos: (string | number)[][] = hijos.length
      ? [
          ['Cliente', 'Trackers', 'Alarmas', 'Cámaras', 'Total'],
          ...hijos.map((h) => [
            h.nombre || h.idCliente || '',
            h.trackers?.cantidad ?? 0,
            h.alarmas?.cantidad ?? 0,
            h.camaras?.cantidad ?? 0,
            h.total ?? 0,
          ]),
        ]
      : [['Sin detalle por cliente en este resumen.']];
    const hojaHijos = XLSX.utils.aoa_to_sheet(filasHijos);
    hojaHijos['!cols'] = [
      { wch: 35 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, hojaResumen, 'Resumen');
    XLSX.utils.book_append_sheet(wb, hojaHijos, 'Detalle por cliente');
    XLSX.writeFile(
      wb,
      `gastos-${label.replace(/\s+/g, '-').toLowerCase()}.xlsx`,
    );
  }

  /** Gráfico apilado: por mes, segmentado por categoría (total = dispositivos). */
  private construirChartHistorico(): void {
    const slots = this.mesesSlots;
    const categorias = slots.map((s) => s.label);
    this.chartHistorico = {
      title: 'Dispositivos por mes',
      icon: 'bar_chart',
      color: 'blue',
      chartHeight: '360px',
      chartOptions: {
        chart: { type: 'column' },
        xAxis: { categories: categorias },
        yAxis: {
          min: 0,
          title: { text: 'Dispositivos' },
          stackLabels: {
            enabled: true,
            // Si el mes no tiene resumen (total 0), lo marca como "Sin resumen".
            formatter: function (this: { total: number }) {
              return this.total > 0
                ? this.total.toLocaleString('es-AR')
                : 'Sin resumen';
            },
          },
        },
        tooltip: {
          shared: true,
        },
        plotOptions: {
          column: { stacking: 'normal', borderRadius: 2 },
        },
        series: [
          {
            name: 'Trackers',
            type: 'column',
            color: '#1565c0',
            data: slots.map(
              (s) => s.resumen?.resumen?.trackers?.cantidadFacturada ?? 0,
            ),
          },
          {
            name: 'Alarmas',
            type: 'column',
            color: '#ef6c00',
            data: slots.map(
              (s) => s.resumen?.resumen?.alarmas?.cantidadFacturada ?? 0,
            ),
          },
          {
            name: 'Cámaras',
            type: 'column',
            color: '#2e7d32',
            data: slots.map(
              (s) => s.resumen?.resumen?.camaras?.cantidadFacturada ?? 0,
            ),
          },
        ],
        legend: { enabled: true },
        credits: { enabled: false },
        exporting: { enabled: false },
      },
    };
  }

  ngOnDestroy(): void {
    // Reservado para suscripciones en vivo (Lista2Service) cuando se implemente.
  }
}
